import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ParkingCircle, Wifi, Users, BedDouble, Coffee, Wine, Bath, Leaf,
  Building2, MapPin, ArrowRight, Star, Quote, Snowflake,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import SearchModule from "../components/SearchModule";
import AccommodationCard from "../components/AccommodationCard";
import BookingDialog from "../components/BookingDialog";
import Reveal from "../components/Reveal";
import { BocauxTeaser } from "../components/BocauxTeaser";
import { SocialProof } from "../components/SocialProof";
import { OnSiteBoutique } from "../components/OnSiteBoutique"; // conservé pour plus tard (boutique désactivée)
import { BikeRental } from "../components/BikeRental";

const serviceIcons = [ParkingCircle, Wifi, Users, BedDouble, Coffee, Wine, Bath, Leaf, Building2, MapPin];

export default function Home() {
  const { t } = useI18n();
  const { settings } = useSite();
  const [acc, setAcc] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [region, setRegion] = useState([]);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get("/accommodations").then((r) => setAcc(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/region").then((r) => setRegion(r.data)).catch(() => {});
  }, []);

  const fig = settings.figures || {};
  const gallery = settings.gallery || [];

  const cards = [
    { t: t("choose.c1t"), x: t("choose.c1x"), img: "/api/files/begaudiere/uploads/657e22d6-5790-4f2e-8e38-87ac0ec8417d.webp", to: "/chambres-hotes", cta: t("common.allRooms"),
      items: ["5 chambres", "Salle d'eau privative", "Petit déjeuner inclus", "Chambres familiales"] },
    { t: t("choose.c2t"), x: t("choose.c2x"), img: "/api/files/begaudiere/uploads/b93ceb72-160a-47b4-85e9-f574a5c025b4.webp", to: "/gites", cta: t("common.allGites"),
      items: ["2 gîtes indépendants", "Cuisines équipées", "Linge de lit", "Parking gratuit"] },
    { t: t("choose.c3t"), x: t("choose.c3x"), img: settings.gallery?.[13] || "/api/files/begaudiere/uploads/51096ab6-e00e-4533-a3ab-103ed39f9ea4.png", to: "/seminaires", cta: t("common.requestQuote"),
      items: ["Séminaires résidentiels", "Journées d'étude", "Hébergement sur place", "Devis personnalisé"] },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[86vh] sm:min-h-[92vh] flex items-center">
        <img src="/api/files/begaudiere/uploads/efc14335-f09f-4f3e-bfa7-e98a2bb1ec2f.webp" alt="Gîtes et chambres d'hôtes du Domaine de la Bégaudière, dans la baie du Mont-Saint-Michel" loading="eager" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-side" />
        <div className="relative mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 pt-28 pb-16 sm:pb-40">
          <div className="max-w-3xl animate-fade-up text-shadow-hero">
            <p className="text-white/85 font-accent text-xl sm:text-2xl italic mb-4">Baie du Mont-Saint-Michel · Bretagne</p>
            <h1 className="font-heading text-white text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] [text-wrap:balance]">{t("hero.title")}</h1>
            <p className="mt-5 text-white/90 font-body text-lg sm:text-xl max-w-2xl">{t("hero.subtitle")}</p>
            <p className="mt-3 text-white/80 font-body max-w-2xl">{t("hero.text")}</p>
            <div className="mt-6 flex flex-wrap gap-2.5" data-testid="hero-amenities">
              {(t("hero.badges") || []).map((b, i) => (
                <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 text-white px-4 py-1.5 text-sm font-medium">
                  {i === 0 && <Snowflake size={15} strokeWidth={1.9} />} {b}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/disponibilites" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 font-medium hover:bg-brand-600 transition-colors hover:scale-[1.02] active:scale-[0.98]">
                {t("common.bookStay")} <ArrowRight size={18} />
              </Link>
              <Link to="/seminaires" data-testid="hero-seminar-btn" className="inline-flex items-center gap-2 rounded-full bg-white/95 text-ink px-7 py-3.5 font-medium hover:bg-white transition-colors">
                {t("common.organizeSeminar")}
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Search module — chevauche le hero sur desktop, en flux sous le hero sur mobile */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-16">
        <div className="mx-auto max-w-5xl">
          <SearchModule variant="hero" />
        </div>
      </div>

      {/* WELCOME */}
      <section className="pt-16 sm:pt-28 pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <p className="text-brand font-accent text-2xl italic mb-3">Bienvenue</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900 mb-6">{t("welcome.title")}</h2>
            <div className="space-y-4 text-ink-600 font-body leading-relaxed">
              <p>{t("welcome.p1")}</p>
              <p>{t("welcome.p2")}</p>
              <p>{t("welcome.p3")}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              {[
                [fig.gites || "3", t("welcome.gites")],
                [fig.chambres || "5", t("welcome.chambres")],
                [`~${fig.capacity_total || "25"}`, t("welcome.capacity")],
                [null, fig.parking || "Parking gratuit"],
                [null, fig.wifi || "Wi-Fi gratuit"],
                [null, "Chambres et gîtes climatisés"],
                [null, fig.groupes || "Espaces groupes et séminaires"],
              ].map(([num, lbl], i) => (
                <div key={i} className={`rounded-2xl p-6 border border-black/5 ${num ? "bg-brand text-white" : "bg-white"}`}>
                  {num && <div className="font-heading text-4xl mb-1">{num}</div>}
                  <div className={`font-body text-sm ${num ? "text-white/85" : "text-ink-600"}`}>{lbl}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <SocialProof />

      {/* CHOOSE */}
      <section className="py-20 lg:py-28 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900">{t("choose.title")}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {cards.map((c, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)] h-full flex flex-col">
                  <div className="relative aspect-[3/2] overflow-hidden">
                    <img src={mediaUrl(c.img)} alt={c.t} loading="lazy" className="img-zoom w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-heading text-2xl text-ink-900 mb-2">{c.t}</h3>
                    <p className="text-ink-600 font-body text-sm leading-relaxed mb-4">{c.x}</p>
                    <ul className="space-y-1.5 mb-6 text-sm text-ink-600">
                      {c.items.map((it) => (
                        <li key={it} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand" />{it}</li>
                      ))}
                    </ul>
                    <Link to={c.to} className="mt-auto inline-flex items-center gap-2 text-brand font-medium hover:gap-3 transition-all" data-testid={`choose-card-${i}`}>
                      {c.cta} <ArrowRight size={17} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-brand font-accent text-2xl italic mb-2">{t("services.subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900">{t("services.title")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-10 gap-x-6">
            {t("services.list").map((label, i) => {
              const Icon = serviceIcons[i % serviceIcons.length];
              return (
                <Reveal key={i} delay={i * 60} className="text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-3">
                    <Icon size={24} strokeWidth={1.5} className="text-brand" />
                  </div>
                  <p className="font-body text-sm text-ink-700">{label}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* LES BOCAUX */}
      <BocauxTeaser className="bg-beige/60" />

      {/* SELECTION */}
      <section className="py-20 lg:py-28 bg-brand-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl">{t("selection.title")}</h2>
              <p className="text-white/70 font-body mt-2">{t("selection.subtitle")}</p>
            </div>
            <div className="flex gap-3">
              <Link to="/chambres-hotes" data-testid="selection-view-chambres" className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-5 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors">{t("nav.chambres")}</Link>
              <Link to="/gites" data-testid="selection-view-gites" className="inline-flex items-center gap-2 rounded-full bg-white text-brand-900 px-5 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors">{t("nav.gites")}</Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {acc.slice(0, 4).map((a) => (
              <div key={a.id} className="text-ink h-full"><AccommodationCard item={a} onBook={setBooking} /></div>
            ))}
          </div>
        </div>
      </section>

      {/* SEMINAR */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="grid grid-cols-2 gap-3">
              {t("seminarSection.formats").map((f) => (
                <div key={f} className="rounded-xl bg-cream border border-black/5 px-4 py-4 text-sm font-body text-ink-700">{f}</div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="order-1 lg:order-2">
            <p className="text-brand font-accent text-2xl italic mb-2">Séminaires et groupes</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900 mb-5">{t("seminarSection.title")}</h2>
            <div className="space-y-4 text-ink-600 font-body leading-relaxed mb-7">
              <p>{t("seminarSection.p1")}</p>
              <p>{t("seminarSection.p2")}</p>
            </div>
            <Link to="/seminaires" data-testid="home-seminar-cta" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 font-medium hover:bg-brand-600 transition-colors">
              {t("seminarSection.cta")} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* REGION */}
      <section className="py-20 lg:py-28 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900 mb-4">{t("regionSection.title")}</h2>
            <p className="text-ink-600 font-body leading-relaxed">{t("regionSection.text")}</p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {region.slice(0, 8).map((r, i) => (
              <Reveal key={r.id} delay={(i % 4) * 80}>
                <Link to={`/la-region/${r.slug}`} className={`group block relative rounded-2xl overflow-hidden ${i === 0 ? "col-span-2 aspect-[2/1]" : "aspect-square"}`} data-testid={`home-region-${r.slug}`}>
                  <img src={r.main_image} alt={r.name} loading="lazy" className="img-zoom w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-4 left-4 font-heading text-white text-xl">{r.name}</span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/la-region" className="inline-flex items-center gap-2 rounded-full border border-brand text-brand px-7 py-3.5 font-medium hover:bg-brand-50 transition-colors" data-testid="home-region-cta">
              {t("regionSection.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <BikeRental />

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-brand font-accent text-2xl italic mb-2">{t("reviews.subtitle")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900">{t("reviews.title")}</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {reviews.slice(0, 4).map((rv) => (
                <div key={rv.id} className="bg-white rounded-2xl p-7 border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)]">
                  <Quote size={26} className="text-brand/30 mb-3" />
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={15} className={i < (rv.rating || 5) ? "fill-terracotta text-terracotta" : "text-ink-400/30"} />
                    ))}
                  </div>
                  <p className="text-ink-600 font-body text-sm leading-relaxed mb-4">“{rv.text}”</p>
                  <div className="text-sm">
                    <span className="font-medium text-ink-900">{rv.author}</span>
                    <span className="text-ink-400"> · {rv.platform} · {rv.stay_type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="py-20 lg:py-28 bg-brand-900">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center max-w-2xl mx-auto mb-12 text-white">
              <p className="text-brand-200 font-accent text-2xl italic mb-2">{t("gallery.subtitle")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl">{t("gallery.title")}</h2>
            </Reveal>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {gallery.slice(0, 12).map((g, i) => (
                <img key={i} src={g} alt={`Domaine de la Bégaudière — photo ${i + 1}, près du Mont-Saint-Michel`} loading="lazy" className="w-full rounded-2xl break-inside-avoid" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-ink-900 mb-5">{t("finalCta.title")}</h2>
            <p className="text-ink-600 font-body text-lg mb-9 max-w-2xl mx-auto">{t("finalCta.subtitle")}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/disponibilites" className="rounded-full bg-brand text-white px-7 py-3.5 font-medium hover:bg-brand-600 transition-colors">{t("finalCta.b1")}</Link>
              <Link to="/chambres-hotes" className="rounded-full border border-brand text-brand px-7 py-3.5 font-medium hover:bg-brand-50 transition-colors">{t("finalCta.b2")}</Link>
              <Link to="/contact" className="rounded-full border border-ink-400/30 text-ink-700 px-7 py-3.5 font-medium hover:bg-black/5 transition-colors">{t("finalCta.b3")}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <BookingDialog item={booking} open={!!booking} onOpenChange={(v) => !v && setBooking(null)} />
    </div>
  );
}
